import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";

export interface ApiSuccess<T> {
  ok: true;
  data: T;
}
export interface ApiError {
  ok: false;
  error: { code: string; message: string; details?: unknown };
}
export type ApiResult<T> = ApiSuccess<T> | ApiError;

export class ApiException extends Error {
  constructor(public code: string, message: string, public status: number, public details?: unknown) {
    super(message);
    this.name = "ApiException";
  }
}

const API_URL = import.meta.env.VITE_API_URL || "";

interface RequestOptions extends RequestInit {
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  auth?: boolean;
}

const buildUrl = (path: string, query?: RequestOptions["query"]) => {
  // API_URL boşsa (same-origin) window.location.origin'i base olarak kullan
  const base = API_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const url = new URL(path.startsWith("http") ? path : `${base}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
};

const REFRESH_URL = API_URL
  ? `${API_URL}/api/auth/refresh`
  : "/api/auth/refresh";

let refreshPromise: Promise<string | null> | null = null;
let lastLockdownToastAt = 0;

async function tryRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(REFRESH_URL, {
        method: "POST",
        credentials: "include",
      });
      const data: ApiResult<{ accessToken: string }> = await res.json();
      if (res.ok && data.ok && data.data.accessToken) {
        useAuthStore.getState().setToken(data.data.accessToken);
        return data.data.accessToken;
      }
      useAuthStore.getState().clear();
      return null;
    } catch {
      useAuthStore.getState().clear();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

/** Video upload gibi özel fetch'ler için: geçerli access token al (gerekirse refresh). */
export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  if (!forceRefresh) {
    const existing = useAuthStore.getState().accessToken;
    if (existing) {
      try {
        const payload = JSON.parse(atob(existing.split(".")[1] || ""));
        const expMs = (payload?.exp ?? 0) * 1000;
        // 60 sn'den fazla ömrü varsa kullan
        if (expMs - Date.now() > 60_000) return existing;
      } catch {
        /* refresh'e düş */
      }
    }
  }
  return tryRefresh();
}

async function request<T>(path: string, opts: RequestOptions = {}, isRetry = false): Promise<T> {
  const { json, query, auth, headers, ...rest } = opts;
  const finalHeaders = new Headers(headers);
  if (json !== undefined) finalHeaders.set("Content-Type", "application/json");

  if (auth) {
    const token = useAuthStore.getState().accessToken;
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  const res = await fetch(buildUrl(path, query), {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
    credentials: "include",
  });

  // Try refresh on 401
  if (res.status === 401 && auth && !isRetry) {
    const newToken = await tryRefresh();
    if (newToken) return request<T>(path, opts, true);
  }

  const text = await res.text();
  let data: ApiResult<T> | null = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* not json */
  }

  if (!res.ok || !data || !data.ok) {
    const code = data && !data.ok ? data.error.code : "HTTP_ERROR";
    const message = data && !data.ok ? data.error.message : res.statusText;
    if (code === "ADMIN_LOCKDOWN") {
      const now = Date.now();
      if (now - lastLockdownToastAt > 8_000) {
        lastLockdownToastAt = now;
        toast.error(message || "Admin paneli geçici olarak kilitli.");
      }
    }
    throw new ApiException(
      code,
      message,
      res.status,
      data && !data.ok ? data.error.details : undefined
    );
  }
  return data.data;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, json?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "POST", json }),
  patch: <T>(path: string, json?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PATCH", json }),
  put: <T>(path: string, json?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PUT", json }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "DELETE" }),
};
